import { computed, Injectable, signal } from '@angular/core';

/**
 * Focus layout for the Creator (contract `ALPHABEES_LAYOUT`, canvas → portal,
 * shared/alphacourse/messages.schema.json): while the course editor needs the
 * whole screen, the portal hides header, menu and hints and the Creator fills
 * the window. Who may switch it is the courses page — it alone knows the
 * canvas iframe and checks origin and source before handing a message here.
 *
 * Two things are kept apart: what canvas asks for (`requested`) and whether
 * the person put the navigation back for now (`paused`). "Navigation
 * einblenden" pauses, "Vollbild" resumes — as long as canvas still asks.
 * Leaving the page or a reload of the iframe ends both; canvas asks, it
 * cannot keep the portal locked.
 */
@Injectable({ providedIn: 'root' })
export class CreatorFocusService {
  private readonly requested = signal(false);
  private readonly paused = signal(false);
  readonly focus = computed(() => this.requested() && !this.paused());
  /** canvas wants the whole window, the person chose the navigation: offer the way back. */
  readonly canResume = computed(() => this.requested() && this.paused());

  /** A message from the canvas iframe; true if it was `ALPHABEES_LAYOUT` of contract 1. */
  apply(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;
    const message = data as { v?: unknown; type?: unknown; payload?: { focus?: unknown } };
    if (message.type !== 'ALPHABEES_LAYOUT' || message.v !== 1) return false;
    const focus = message.payload?.focus;
    if (typeof focus !== 'boolean') return false;
    this.requested.set(focus);
    this.paused.set(false); // each request of canvas starts fresh
    return true;
  }

  /** "Navigation einblenden": the portal's own way out, canvas keeps asking. */
  pause(): void {
    this.paused.set(true);
  }

  /** "Vollbild": back into focus while canvas still asks for it. */
  resume(): void {
    this.paused.set(false);
  }

  /** Leaving the page, a reload of the creator: nothing asks any more. */
  end(): void {
    this.requested.set(false);
    this.paused.set(false);
  }
}
